import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import BusinessLogic from '../../utils/BusinessLogic';

/**
 * CalculatorContext - Context for managing arbitrage calculator state
 * 
 * Features:
 * - Calculator input state management (stakes, odds, total investment)
 * - Calculation state management and validation
 * - Calculation history and recent calculations
 * - Reset and clear functionality
 * - Error states and edge case handling
 * - Arbitrage calculation logic
 */

// Default calculator state
const DEFAULT_CALCULATOR_STATE = {
  // Input state
  inputs: {
    totalStake: '',
    odds: [],
    bookmakers: [],
    marketType: '',
    eventDetails: null,
    fixedMode: 'total',
    fixedStakeIndex: null,
    fixedStakeValue: null,
    anchoredTotal: null,
    manualStakes: {},
    derivedStakeIndex: null,
  },
  
  // Calculation state
  calculations: {
    stakeDistribution: [],
    guaranteedProfit: 0,
    profitPercentage: 0,
    totalReturn: 0,
    profitPerPosition: [],
    isValid: false,
    error: null,
  },
  
  // UI state
  ui: {
    isCalculating: false,
    showResults: false,
    activeTab: 'calculator',
    isModalOpen: false,
    modalData: null,
    modalSessionKey: null,
  },
  
  // History state
  history: {
    recentCalculations: [],
    maxHistoryItems: 10,
  },
  
  // Validation state
  validation: {
    errors: {},
    warnings: {},
    isValid: false,
  },
};

const DEFAULT_SESSION_STAKE = '100';

const buildEventDetailsFromModalData = (event) => {
  if (!event) {
    return null;
  }

  return {
    teamOne: event.teamOne,
    teamTwo: event.teamTwo,
    date: event.date,
    time: event.time,
    championship: event.championship,
    sport: event.sport,
  };
};

const buildInputsFromModalData = (modalData) => {
  const baseInputs = {
    ...DEFAULT_CALCULATOR_STATE.inputs,
    totalStake: DEFAULT_SESSION_STAKE,
    fixedMode: 'total',
    fixedStakeIndex: null,
    fixedStakeValue: null,
    anchoredTotal: null,
    manualStakes: {},
    derivedStakeIndex: null,
  };

  if (!modalData) {
    return baseInputs;
  }

  return {
    ...baseInputs,
    odds: modalData.odds || [],
    marketType: modalData.marketType || '',
    eventDetails: buildEventDetailsFromModalData(modalData.event),
  };
};

const getModalSessionKey = (modalData) => {
  if (!modalData?.event) {
    return null;
  }

  return `${modalData.event.id}-${modalData.marginKey || modalData.marketType}`;
};

// Create context
const CalculatorContext = createContext({
  // State
  ...DEFAULT_CALCULATOR_STATE,
  
  // Actions
  actions: {
    setTotalStake: () => {},
    setOdds: () => {},
    setBookmakers: () => {},
    setMarketType: () => {},
    setEventDetails: () => {},
    setFixedStake: () => {},
    setManualStake: () => {},
    clearFixedStake: () => {},
    initializeCalculatorSession: () => {},
    resetToInitialSession: () => {},
    calculateArbitrage: () => {},
    resetCalculator: () => {},
    clearCalculator: () => {},
    addToHistory: () => {},
    clearHistory: () => {},
    setActiveTab: () => {},
    validateInputs: () => {},
  },
});

/**
 * CalculatorProvider - Provider component for calculator context
 */
export const CalculatorProvider = ({ children }) => {
  // State management
  const [inputs, setInputs] = useState(DEFAULT_CALCULATOR_STATE.inputs);
  const [calculations, setCalculations] = useState(DEFAULT_CALCULATOR_STATE.calculations);
  const [ui, setUI] = useState(DEFAULT_CALCULATOR_STATE.ui);
  const [history, setHistory] = useState(DEFAULT_CALCULATOR_STATE.history);
  const [validation, setValidation] = useState(DEFAULT_CALCULATOR_STATE.validation);
  const inputsRef = useRef(inputs);

  useEffect(() => {
    inputsRef.current = inputs;
  }, [inputs]);

  // Load history from localStorage on mount
  useEffect(() => {
    try {
      const savedHistory = localStorage.getItem('arbiwin_calculator_history');
      if (savedHistory) {
        const parsedHistory = JSON.parse(savedHistory);
        setHistory(prev => ({
          ...prev,
          recentCalculations: parsedHistory.recentCalculations || [],
        }));
      }
    } catch (error) {
      console.error('❌ CalculatorContext: Error loading history from localStorage:', error);
    }
  }, []);

  // Save history to localStorage when it changes
  useEffect(() => {
    try {
      localStorage.setItem('arbiwin_calculator_history', JSON.stringify({
        recentCalculations: history.recentCalculations,
        lastUpdated: new Date().toISOString(),
      }));
    } catch (error) {
      console.error('❌ CalculatorContext: Error saving history to localStorage:', error);
    }
  }, [history.recentCalculations]);

  // Input validation
  const validateInputs = useCallback(() => {
    const errors = {};
    const warnings = {};
    let isValid = true;

    // Only validate essential fields - be very permissive
    if (inputs.fixedMode === 'total') {
      const totalStake = parseFloat(inputs.totalStake);
      if (!inputs.totalStake || isNaN(totalStake) || totalStake <= 0) {
        errors.totalStake = 'Total stake must be a positive number';
        isValid = false;
      }
    }

    if (inputs.fixedMode === 'totalAnchored') {
      const anchoredTotal = parseFloat(inputs.anchoredTotal);
      if (!inputs.anchoredTotal || isNaN(anchoredTotal) || anchoredTotal <= 0) {
        errors.anchoredTotal = 'Anchored total must be a positive number';
        isValid = false;
      }

      const manualEntries = Object.entries(inputs.manualStakes || {});
      if (manualEntries.length === 0) {
        errors.manualStakes = 'At least one manual stake is required';
        isValid = false;
      }

      let manualSum = 0;
      manualEntries.forEach(([rawIndex, rawValue]) => {
        const stake = parseFloat(rawValue);
        if (!rawValue || isNaN(stake) || stake <= 0) {
          errors[`stake_${rawIndex}`] = 'Manual stake must be a positive number';
          isValid = false;
        } else {
          manualSum += stake;
        }
      });

      const oddsCount = inputs.odds?.length || 0;
      const manualCount = manualEntries.length;

      if (!isNaN(anchoredTotal)) {
        if (manualCount < oddsCount && manualSum > anchoredTotal + 0.01) {
          errors.manualStakes = 'Manual stakes exceed anchored total';
          isValid = false;
        }

        if (manualCount === oddsCount && Math.abs(manualSum - anchoredTotal) > 0.01) {
          errors.manualStakesSum = 'Manual stakes must equal anchored total';
          isValid = false;
        }
      }
    }

    // Only check if odds exist and have values - don't validate bookmaker/outcome
    if (!inputs.odds || inputs.odds.length === 0) {
      errors.odds = 'At least one odd is required';
      isValid = false;
    } else {
      inputs.odds.forEach((odd, index) => {
        if (!odd.value || isNaN(parseFloat(odd.value)) || parseFloat(odd.value) <= 1) {
          errors[`odds_${index}`] = `Odd ${index + 1} must be greater than 1`;
          isValid = false;
        }
      });
    }

    if (inputs.fixedMode === 'stake' && inputs.fixedStakeIndex !== null) {
      const fixedStake = parseFloat(inputs.fixedStakeValue);
      if (!inputs.fixedStakeValue || isNaN(fixedStake) || fixedStake <= 0) {
        errors[`stake_${inputs.fixedStakeIndex}`] = 'Fixed stake must be a positive number';
        isValid = false;
      }
    }

    setValidation({
      errors,
      warnings,
      isValid,
    });

    return isValid;
  }, [inputs]);

  // Auto-validate inputs when they change
  useEffect(() => {
    validateInputs();
  }, [inputs, validateInputs]);

  const mapBusinessLogicResult = useCallback((result) => {
    if (!result.distribution || result.distribution.length === 0) {
      return {
        stakeDistribution: [],
        guaranteedProfit: 0,
        profitPercentage: 0,
        totalReturn: 0,
        profitPerPosition: [],
        isValid: false,
        error: result.error,
        totalImpliedProbability: result.totalImpliedProbability || 0,
      };
    }

    const stakeDistribution = result.distribution.map((item) => ({
      index: item.index,
      bookmaker: item.bookmaker,
      outcome: item.outcome,
      odds: item.odds,
      stake: item.stake,
      potentialReturn: item.potentialReturn,
      profit: item.profit,
    }));

    const profitPerPosition = [];
    stakeDistribution.forEach((item, arrayIndex) => {
      const positionIndex = typeof item.index === 'number' ? item.index : arrayIndex;
      profitPerPosition[positionIndex] = item.profit;
    });

    return {
      stakeDistribution,
      guaranteedProfit: result.guaranteedProfit ?? 0,
      profitPercentage: result.guaranteedProfitPercentage ?? 0,
      totalReturn: BusinessLogic.roundToPrecision(
        (result.totalStake ?? 0) + (result.guaranteedProfit ?? 0),
        2,
      ),
      profitPerPosition,
      isValid: result.isValid,
      error: result.error,
      totalImpliedProbability: result.totalImpliedProbability,
    };
  }, []);

  // Arbitrage calculation logic
  const calculateArbitrage = useCallback(() => {
    const currentInputs = inputsRef.current;

    setUI(prev => ({ ...prev, isCalculating: true }));

    try {
      const odds = currentInputs.odds.map((odd) => ({
        ...odd,
        value: parseFloat(odd.value),
      }));

      let result;

      if (
        currentInputs.fixedMode === 'totalAnchored'
        && currentInputs.anchoredTotal !== null
        && currentInputs.anchoredTotal !== ''
        && Object.keys(currentInputs.manualStakes || {}).length > 0
      ) {
        result = BusinessLogic.calculateStakeDistributionFromAnchoredTotal(
          odds,
          parseFloat(currentInputs.anchoredTotal),
          currentInputs.manualStakes,
        );
      } else if (
        currentInputs.fixedMode === 'stake'
        && currentInputs.fixedStakeIndex !== null
        && currentInputs.fixedStakeValue !== null
        && currentInputs.fixedStakeValue !== ''
      ) {
        result = BusinessLogic.calculateStakeDistributionFromFixedStake(
          odds,
          currentInputs.fixedStakeIndex,
          parseFloat(currentInputs.fixedStakeValue),
        );
      } else {
        const totalStake = parseFloat(currentInputs.totalStake);
        result = BusinessLogic.calculateStakeDistribution(odds, totalStake);
      }

      const calculationResult = mapBusinessLogicResult(result);

      setCalculations(calculationResult);
      setUI(prev => ({ ...prev, isCalculating: false, showResults: true }));

      if (currentInputs.fixedMode === 'stake' && result.isValid) {
        const nextTotalStake = String(result.totalStake);
        setInputs((prev) => (
          prev.totalStake === nextTotalStake
            ? prev
            : { ...prev, totalStake: nextTotalStake }
        ));
      }

      return calculationResult;
    } catch (error) {
      console.error('❌ CalculatorContext: Error in arbitrage calculation:', error);

      setCalculations((prev) => ({
        ...prev,
        isValid: false,
        error: error.message || 'Calculation error occurred',
      }));

      setUI((prev) => ({ ...prev, isCalculating: false, showResults: false }));
    }
  }, [mapBusinessLogicResult]);

  // Set total stake
  const setTotalStake = useCallback((stake) => {
    setInputs((prev) => (
      prev.totalStake === stake ? prev : { ...prev, totalStake: stake }
    ));
  }, []);

  // Set odds
  const setOdds = useCallback((odds) => {
    setInputs(prev => ({ ...prev, odds }));
  }, []);

  // Set bookmakers
  const setBookmakers = useCallback((bookmakers) => {
    setInputs(prev => ({ ...prev, bookmakers }));
  }, []);

  // Set market type
  const setMarketType = useCallback((marketType) => {
    setInputs(prev => ({ ...prev, marketType }));
  }, []);

  // Set event details
  const setEventDetails = useCallback((eventDetails) => {
    setInputs(prev => ({ ...prev, eventDetails }));
  }, []);

  // Lock stake on a specific position and switch to fixed-stake calculation mode
  const setFixedStake = useCallback((index, value) => {
    setInputs((prev) => {
      const parsedValue = parseFloat(value);
      const hasValidValue = value !== '' && !isNaN(parsedValue) && parsedValue > 0;

      if (
        prev.fixedMode === 'stake'
        && prev.fixedStakeIndex !== null
        && index !== prev.fixedStakeIndex
        && hasValidValue
      ) {
        let anchoredTotal = parseFloat(prev.totalStake);

        if (
          (isNaN(anchoredTotal) || anchoredTotal <= 0)
          && prev.fixedStakeValue
        ) {
          anchoredTotal = parseFloat(prev.fixedStakeValue);
        }

        const fixedStakeResult = BusinessLogic.calculateStakeDistributionFromFixedStake(
          prev.odds.map((odd) => ({
            ...odd,
            value: parseFloat(odd.value),
          })),
          prev.fixedStakeIndex,
          parseFloat(prev.fixedStakeValue),
        );

        if (fixedStakeResult.isValid && fixedStakeResult.totalStake > 0) {
          anchoredTotal = fixedStakeResult.totalStake;
        }

        if (isNaN(anchoredTotal) || anchoredTotal <= 0) {
          return prev;
        }

        return {
          ...prev,
          fixedMode: 'totalAnchored',
          anchoredTotal: String(anchoredTotal),
          totalStake: String(anchoredTotal),
          manualStakes: {
            [prev.fixedStakeIndex]: prev.fixedStakeValue,
            [index]: value,
          },
          fixedStakeIndex: null,
          fixedStakeValue: null,
          derivedStakeIndex: null,
        };
      }

      return {
        ...prev,
        fixedMode: 'stake',
        fixedStakeIndex: index,
        fixedStakeValue: value,
        anchoredTotal: null,
        manualStakes: {},
        derivedStakeIndex: null,
      };
    });
  }, []);

  const setManualStake = useCallback((index, value) => {
    setInputs((prev) => {
      if (prev.fixedMode !== 'totalAnchored') {
        return prev;
      }

      return {
        ...prev,
        manualStakes: {
          ...prev.manualStakes,
          [index]: value,
        },
      };
    });
  }, []);

  // Return to optimal distribution from total budget
  const clearFixedStake = useCallback(() => {
    setInputs((prev) => ({
      ...prev,
      fixedMode: 'total',
      fixedStakeIndex: null,
      fixedStakeValue: null,
      anchoredTotal: null,
      manualStakes: {},
      derivedStakeIndex: null,
    }));
  }, []);

  // Reset calculator to default state
  const resetCalculator = useCallback(() => {
    setInputs(DEFAULT_CALCULATOR_STATE.inputs);
    setCalculations(DEFAULT_CALCULATOR_STATE.calculations);
    setUI(DEFAULT_CALCULATOR_STATE.ui);
    setValidation(DEFAULT_CALCULATOR_STATE.validation);
  }, []);

  const initializeCalculatorSession = useCallback((modalData) => {
    setInputs(buildInputsFromModalData(modalData));
    setCalculations(DEFAULT_CALCULATOR_STATE.calculations);
    setValidation(DEFAULT_CALCULATOR_STATE.validation);
    setUI((prev) => ({
      ...prev,
      showResults: false,
      isCalculating: false,
      modalSessionKey: getModalSessionKey(modalData),
    }));
  }, []);

  const resetToInitialSession = useCallback(() => {
    setInputs(buildInputsFromModalData(ui.modalData));
    setCalculations(DEFAULT_CALCULATOR_STATE.calculations);
    setValidation(DEFAULT_CALCULATOR_STATE.validation);
    setUI((prev) => ({
      ...prev,
      showResults: false,
      isCalculating: false,
    }));
  }, [ui.modalData]);

  // Clear calculator (keep history)
  const clearCalculator = useCallback(() => {
    setInputs(prev => ({
      ...DEFAULT_CALCULATOR_STATE.inputs,
      marketType: prev.marketType, // Keep market type
      eventDetails: prev.eventDetails, // Keep event details
    }));
    setCalculations(DEFAULT_CALCULATOR_STATE.calculations);
    setUI(prev => ({ ...prev, showResults: false }));
    setValidation(DEFAULT_CALCULATOR_STATE.validation);
  }, []);

  // Add calculation to history
  const addToHistory = useCallback((calculation) => {
    const historyItem = {
      id: Date.now().toString(),
      timestamp: new Date().toISOString(),
      inputs: { ...inputs },
      calculations: { ...calculation },
      marketType: inputs.marketType,
      eventDetails: inputs.eventDetails,
    };

    setHistory(prev => ({
      ...prev,
      recentCalculations: [
        historyItem,
        ...prev.recentCalculations.slice(0, prev.maxHistoryItems - 1),
      ],
    }));

    return historyItem;
  }, [inputs]);

  // Clear history
  const clearHistory = useCallback(() => {
    setHistory(prev => ({
      ...prev,
      recentCalculations: [],
    }));
  }, []);

  // Set active tab
  const setActiveTab = useCallback((tab) => {
    setUI(prev => ({ ...prev, activeTab: tab }));
  }, []);

  // Modal management functions
  const openModal = useCallback((modalData) => {
    setInputs(buildInputsFromModalData(modalData));
    setCalculations(DEFAULT_CALCULATOR_STATE.calculations);
    setValidation(DEFAULT_CALCULATOR_STATE.validation);
    setUI((prev) => ({
      ...prev,
      isModalOpen: true,
      modalData,
      modalSessionKey: getModalSessionKey(modalData),
      showResults: false,
      isCalculating: false,
    }));
  }, []);

  const closeModal = useCallback(() => {
    setInputs(DEFAULT_CALCULATOR_STATE.inputs);
    setCalculations(DEFAULT_CALCULATOR_STATE.calculations);
    setValidation(DEFAULT_CALCULATOR_STATE.validation);
    setUI((prev) => ({
      ...prev,
      isModalOpen: false,
      modalData: null,
      modalSessionKey: null,
      showResults: false,
      isCalculating: false,
    }));
  }, []);

  // Computed values
  const computedValues = useMemo(() => {
    const totalStake = parseFloat(inputs.totalStake) || 0;
    const oddsCount = inputs.odds?.length || 0;
    const hasValidInputs = validation.isValid;
    const hasCalculations = calculations.isValid && calculations.stakeDistribution.length > 0;
    const isFixedStakeMode = inputs.fixedMode === 'stake' && inputs.fixedStakeIndex !== null;
    const isTotalAnchoredMode = inputs.fixedMode === 'totalAnchored' && inputs.anchoredTotal !== null;
    const fixedStake = parseFloat(inputs.fixedStakeValue) || 0;
    const anchoredTotal = parseFloat(inputs.anchoredTotal) || 0;
    const manualStakeCount = Object.keys(inputs.manualStakes || {}).length;
    const canCalculate = hasValidInputs && oddsCount > 0 && (
      isTotalAnchoredMode
        ? anchoredTotal > 0 && manualStakeCount > 0
        : isFixedStakeMode
          ? fixedStake > 0
          : totalStake > 0
    );

    return {
      totalStake,
      oddsCount,
      hasValidInputs,
      hasCalculations,
      canCalculate,
      isFixedStakeMode,
      isTotalAnchoredMode,
      fixedStakeIndex: inputs.fixedStakeIndex,
      anchoredTotal,
      manualStakeCount,
      historyCount: history.recentCalculations.length,
    };
  }, [inputs, validation, calculations, history]);

  // Memoize actions separately to prevent unnecessary re-renders
  // Memoized actions object - simplified to prevent circular dependencies
  const actions = useMemo(() => ({
    setTotalStake,
    setOdds,
    setBookmakers,
    setMarketType,
    setEventDetails,
    setFixedStake,
    setManualStake,
    clearFixedStake,
    initializeCalculatorSession,
    resetToInitialSession,
    calculateArbitrage,
    resetCalculator,
    clearCalculator,
    addToHistory,
    clearHistory,
    setActiveTab,
    validateInputs,
    openModal,
    closeModal,
  }), [
    // Only include stable dependencies to prevent circular re-renders
    setTotalStake,
    setOdds,
    setBookmakers,
    setMarketType,
    setEventDetails,
    setFixedStake,
    setManualStake,
    clearFixedStake,
    initializeCalculatorSession,
    resetToInitialSession,
    resetCalculator,
    clearCalculator,
    addToHistory,
    clearHistory,
    setActiveTab,
    openModal,
    closeModal,
    calculateArbitrage,
    validateInputs,
  ]);

  // Context value
  const contextValue = useMemo(() => ({
    // State
    inputs,
    calculations,
    ui,
    history,
    validation,
    computed: computedValues,
    
    // Actions
    actions,
  }), [
    inputs,
    calculations,
    ui,
    history,
    validation,
    computedValues,
    actions,
  ]);

  return (
    <CalculatorContext.Provider value={contextValue}>
      {children}
    </CalculatorContext.Provider>
  );
};

CalculatorProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * useCalculator - Custom hook to access calculator context
 */
export const useCalculator = () => {
  const context = useContext(CalculatorContext);
  
  if (!context) {
    throw new Error('useCalculator must be used within a CalculatorProvider');
  }
  
  return context;
};

export default CalculatorContext;
